import {
  Box,
  Button,
  Icon,
  Menu,
  MenuItem,
  MenuPopup,
  MenuTrigger,
  SidebarPane,
  SidebarPaneBody,
  SidebarPaneFooter,
  SidebarPaneHeader,
  SidebarPaneTitle,
  SidebarSeparator,
  Text,
  useIsMobile,
  type IconName,
} from "@salestrak/ui";
import * as React from "react";

import {
  CustomizeNavigationDialog,
  type CustomizeNavigationTab,
} from "#app/components/navigation/module-rail/customize-navigation-dialog";
import { useCollapseSidebar } from "#app/lib/hooks/use-sidebar-collapsed";
import { useSidebarResize } from "#app/components/navigation/use-sidebar-resize";
import type { Omittable } from "@salestrak/ui";

/**
 * ModuleMenu — the module menus that fill the sidebar pane (Home, Workflows,
 * Teams). The pane shell, its gutter and its scroll behaviour are the
 * library's `SidebarPane`; this fills its three slots with domain content:
 *
 *  - Header: module-menu title, a collapse control (double-left chevron)
 *    revealed on header hover, and a context menu whose options come from the
 *    individual module menu.
 *  - Body: the instance-defined, ordered section list, separated by dividers.
 *  - Footer (opt-in via `customizeNav`): a pinned Customize Navigation
 *    button, revealed on pane hover like the collapse control.
 *
 * Each module-menu instance defines its own `sections` array. Section `id`s
 * key the user's section-order preference — instances must keep them stable.
 */

export type ModuleMenuSection = {
  /** Stable id — future user preferences key order/visibility on this. */
  id: string;
  /** Section body, rendered in array order. */
  Component: React.ComponentType;
};

export type ModuleMenuOption = Omittable<{
  id: string;
  label: string;
  icon?: IconName;
  onClick: () => void;
}>;

type ModuleMenuProps = Omittable<{
  title: string;
  /** Ordered section list, defined by the module-menu instance. */
  sections: ModuleMenuSection[];
  /** Options for the header's context menu. Omit to hide the trigger. */
  options?: ModuleMenuOption[];
  /** Opt-in pinned footer button opening the Customize Navigation dialog at
   * the given tab. Omit to hide (the default). */
  customizeNav?: { initialTab: CustomizeNavigationTab };
}>;

export function ModuleMenu({
  title,
  sections,
  options,
  customizeNav,
}: ModuleMenuProps) {
  const isMobile = useIsMobile();
  const collapseSidebar = useCollapseSidebar();
  const sidebarResize = useSidebarResize();
  const [customizeOpen, setCustomizeOpen] = React.useState(false);

  return (
    <SidebarPane
      // In the mobile drawer the pane is free-standing — nothing to meet, so
      // every corner rounds.
      roundedSide={isMobile ? undefined : "left"}
      // The pane drags its own right edge.
      {...sidebarResize}
    >
      <SidebarPaneHeader
        actions={
          <>
            {/* Collapsing is a desktop-only notion: on mobile the pane lives in
              * a drawer, which dismisses instead of collapsing. */}
            {!isMobile && (
              <Box
                hoverReveal="header"
                revealOnTouch
                render={
                  <Button
                    aria-label="Collapse sidebar"
                    onClick={collapseSidebar}
                    size="icon-sm"
                    variant="ghost"
                  />
                }
              >
                <Icon name="chevrons-left" size="sm" />
              </Box>
            )}
            {options != null && options.length > 0 && (
              <Menu>
                <MenuTrigger
                  render={
                    <Button
                      aria-label={`${title} actions`}
                      size="sm"
                      variant="outline"
                    />
                  }
                >
                  <Icon name="plus" size="sm" />
                  <Icon color="muted" name="chevron-down" size="sm" />
                </MenuTrigger>
                {/* Compact menu hugging the trigger: its top-right corner sits
                 * on the trigger's bottom-right corner. */}
                <MenuPopup align="end" className="min-w-40" side="bottom">
                  {options.map((option, index) => (
                    <MenuItem
                      key={`${option.id}-${index}`}
                      onClick={option.onClick}
                    >
                      {option.icon != null && (
                        <Icon color="muted" name={option.icon} size="sm" />
                      )}
                      <Text>{option.label}</Text>
                    </MenuItem>
                  ))}
                </MenuPopup>
              </Menu>
            )}
          </>
        }
      >
        <SidebarPaneTitle>{title}</SidebarPaneTitle>
      </SidebarPaneHeader>

      <SidebarPaneBody>
        {sections.map((section, index) => (
          <React.Fragment key={`${section.id}-${index}`}>
            {index > 0 && <SidebarSeparator />}
            <section.Component />
          </React.Fragment>
        ))}
      </SidebarPaneBody>

      {customizeNav != null && (
        <>
          <SidebarPaneFooter>
            <Box
              fill="width"
              hoverReveal="pane"
              revealOnTouch
              render={
                <Button onClick={() => setCustomizeOpen(true)} variant="outline" />
              }
            >
              <Icon name="settings" size="sm" />
              <Text truncate>Customize Navigation</Text>
            </Box>
          </SidebarPaneFooter>
          <CustomizeNavigationDialog
            initialTab={customizeNav.initialTab}
            onOpenChange={setCustomizeOpen}
            open={customizeOpen}
          />
        </>
      )}
    </SidebarPane>
  );
}
